/**
 * BigQuery event loggers for the CPT codes page: save-button clicks
 * and report-inaccuracy clicks.
 *
 * @module useCptAnalytics
 */
import { useCallback } from "react";
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { User } from "../../../../../data-model/firebaseAuthModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { EventsName } from "../../../../../enums/eventsName";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../../workerManagers/workerManager";

/**
 * Inputs required to build CPT page analytics payloads.
 */
interface UseCptAnalyticsParams {
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  authOnFileDetails: AuthOnFile | null | undefined;
  user: User | null | undefined;
  payerName: string;
  tableData: CptCodesTableRowModel[];
}

/**
 * Provides memoised BigQuery logging callbacks for CPT code page actions.
 *
 * @param params - Order, user, payer, and table data required for event payloads
 * @returns `handleSaveLog` and `logReportInaccuracyClicked`
 */
export const useCptAnalytics = ({
  generalOrderDetails,
  authOnFileDetails,
  user,
  payerName,
  tableData,
}: UseCptAnalyticsParams) => {
  const handleSaveLog = useCallback(() => {
    apiWorkerManager.enqueueAndProcess({
      patient_id:
        generalOrderDetails?.demographics?.patient_id ??
        generalOrderDetails?.demographics?.patient_mrn ??
        "",
      order_id: generalOrderDetails?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.SAVE_BUTTON_CLICKED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
        auth_status: authOnFileDetails?.auth_status ?? "",
        payer_name: payerName,
      },
    });
  }, [generalOrderDetails, authOnFileDetails, user, payerName, tableData]);

  const logReportInaccuracyClicked = useCallback(() => {
    apiWorkerManager.enqueueAndProcess({
      patient_id:
        generalOrderDetails?.demographics?.patient_id ??
        generalOrderDetails?.demographics?.patient_mrn ??
        "",
      order_id: generalOrderDetails?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      event_name: EventsName.REPORT_INACCURACY_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
        payer_name: payerName,
      },
    });
  }, [generalOrderDetails, user, payerName]);

  return { handleSaveLog, logReportInaccuracyClicked };
};
